import type { ReturnSchedulingRequestView } from '@/dashboard/api';
/**
 * PATIENT-360-AND-EHNOTE-COEXISTENCE-SPINE-V1 (V3-P5
 * PATIENT-360-SPINE-CONNECT-V1) -- pure helpers for the Patient 360
 * surface (`/dashboard/patients/:patientId`).
 *
 * Pure by construction -- no React, no fetch, no Supabase, no `Date.now()`
 * (callers inject `nowMs`) -- the patient-master-view.ts doctrine, so every
 * label map and derivation unit-tests without jsdom.
 *
 * CONTRACT MIRRORS (separate-packages convention, kept in lockstep by
 * review): the wire types below mirror api/routes/patient-master-360.ts
 * + lib/domain/patient/patient-360.ts.
 *
 * ALIAS LAW (package section 1): patient_identifiers rows render as
 * ALIASES under the ONE org-level patient identity -- read-only, labeled
 * by their REAL system/source, external systems explicitly marked. The
 * INTERNAL bridge rows ('lead_id' -- the patient->lead journey bridge --
 * and 'adoracare_umr' -- the primary identity itself) are NOT external MR
 * aliases and never render in the alias strip. One AdoraCare patient +
 * zero or more lawful aliases; never multiple patient identities.
 *
 * SECURITY POSTURE: nothing here is an access decision; the backend route
 * gates remain the boundary. No phone value ever enters this module --
 * contact stays on the EXISTING masked record read + audited reveal.
 */

import { clinicLabelOf } from '@/dashboard/clinic-context';
import { formatMinorUnitsInr } from '@/dashboard/billing/billing-entry-view';
import { statusChipLabel } from '@/dashboard/ui/status-canon';
import type { AcBadgeTone } from '@/dashboard/ui/AcBadge';

// --- Wire contract mirrors (api/routes/patient-master-360.ts) -------------

export type IntakeEvidenceWire =
  | {
      readonly availability: 'available';
      readonly status: 'none' | 'in_progress' | 'completed';
      readonly mode: 'digital' | 'paper' | null;
    }
  | { readonly availability: 'unavailable' };

export type ClinicalOperationalState =
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'unavailable';

export interface Patient360AppointmentWire {
  readonly appointmentId: number;
  readonly clinicId: number;
  readonly clinicLabel: string | null;
  readonly doctorName: string | null;
  readonly scheduledAt: string;
  readonly status: string;
  readonly canonicalStatus: string | null;
  readonly linkage: 'direct' | 'lead_linked';
  readonly arrivedAt: string | null;
  readonly intake: IntakeEvidenceWire;
  readonly consultStarted: boolean;
  /** VISIT-ACTOR-ATTRIBUTION-V1 (#277 / FND-14): the attributed actor of the
   *  consult (role + honest display label); null = a pre-attribution visit. */
  readonly consultActor: { readonly role: string; readonly label: string } | null;
  readonly clinicalState: ClinicalOperationalState;
  readonly paymentRecorded: boolean;
  readonly isFollowUp: boolean;
}

export interface Patient360PaymentItemWire {
  readonly paymentId: number;
  readonly amountMinorUnits: number;
  readonly currency: string;
  readonly occurredAt: string;
  readonly clinicId: number;
  readonly appointmentId: number | null;
}

/** One VOIDED payment on the history lane (#273 / FND-01). The supervisor-tier
 * fields (voidReason / voidedByLabel) are ABSENT on the non-supervisor wire
 * (the route strips them for roles outside BILLING_SUPERVISOR_ROLES). Optional
 * so an older payload keeps rendering. */
export interface Patient360VoidedPaymentItemWire {
  readonly paymentId: number;
  readonly amountMinorUnits: number;
  readonly currency: string;
  readonly method: string | null;
  readonly occurredAt: string;
  readonly voidedAt: string | null;
  readonly voidReason?: string | null;
  readonly voidedByLabel?: string | null;
}

export type PaymentHistoryStateWire = 'has_voided' | 'none' | 'unavailable';

/** Wire mirror of the backend Patient360PaymentHistory. */
export interface Patient360PaymentHistoryWire {
  readonly state: PaymentHistoryStateWire;
  readonly voided: readonly Patient360VoidedPaymentItemWire[];
}

export type PaymentEvidenceState =
  | 'recorded'
  | 'multiple_recorded'
  | 'none_recorded'
  | 'unavailable';

export type FollowUpEvidenceState =
  | 'required'
  | 'scheduled'
  | 'completed'
  | 'none'
  | 'unavailable';

export type Patient360PrimaryActionToken =
  | 'review_required'
  | 'mark_arrived'
  | 'start_intake'
  | 'continue_intake'
  | 'check_in'
  | 'record_payment'
  | 'schedule_follow_up'
  | 'no_action_required';

export type PatientJourneySegmentKey =
  | 'booked'
  | 'arrived'
  | 'intake'
  | 'checked_in'
  | 'ready_for_doctor'
  | 'consultation'
  | 'checkout'
  | 'follow_up';

export type PatientJourneySegmentState =
  | 'complete'
  | 'current'
  | 'pending'
  | 'not_evidenced'
  | 'evidenced'
  | 'unavailable'
  | 'not_applicable';

/** THE CONVERSION VOCABULARY (corrective 2026-07-14): mirror of the
 * backend CommercialConversionState + the honest 'unavailable' (accepted
 * items exist but the payment-evidence lane is unreadable). Converted
 * tokens ONLY ever arrive behind active non-voided first-payment evidence
 * -- the backend derivation is the boundary; this mirror renders it. */
export type CommercialConversionStateWire =
  | 'no_treatment_recommended'
  | 'undecided'
  | 'accepted_payment_pending'
  | 'partially_accepted_payment_pending'
  | 'declined'
  | 'deferred'
  | 'converted_first_payment'
  | 'converted_chief_complaint'
  | 'converted_full_package'
  | 'unavailable';

/** Wire mirror of the backend Patient360TreatmentCheckoutSummary
 * (lib/adapters/supabase/patient-360-read-model.ts). */
export interface Patient360TreatmentCheckoutWire {
  readonly availability: 'available' | 'unavailable';
  /** OPTIONAL (additive contract -- pre-corrective payloads keep
   * rendering); absent renders as the honest unavailable label. */
  readonly commercialConversionState?: CommercialConversionStateWire;
  readonly recommendedItemCount: number;
  readonly acceptedItemCount: number;
  readonly declinedItemCount: number;
  readonly deferredItemCount: number;
  readonly undecidedItemCount: number;
  readonly recommendedValueInr: number | null;
  readonly acceptedEstimateInr: number | null;
  readonly unknownPriceItemCount: number;
  readonly discount: {
    readonly state: 'none' | 'applied' | 'unavailable';
    readonly activeCount: number;
    readonly discountAmountInrTotal: number | null;
    readonly finalAcceptedValueInrTotal: number | null;
  };
}

/** Wire mirror of the backend Patient360TreatmentJourneyLane
 * (MULTI-VISIT-TREATMENT-JOURNEY-V1, V6-P4). AGGREGATE-GRADE only: counts
 * + the course-grain money roll-up (THE billing-queue netting law at
 * course grain -- a PLAN-remainder obligation, NEVER an account balance;
 * payments.balance stays 'not_available'). NO course name, NO procedure
 * names on this role-identical wire.
 *
 * PRESENTATION NOTE (PATIENT-360-FOUR-FLAG-CORRECTIVE-V1 FLAG 1): the
 * sentence above describes THE WIRE and is still exact -- the wire is
 * unchanged and `payments.balance` really does stay 'not_available'. What
 * the founder ruling changed is what the PAGE does with it: the derivable
 * remainder is now rendered ONCE, in the money region, as that region's
 * account line (see derivePlanRemainderLines). Do not read this wire
 * comment as a rule that the page must render nothing. */
export interface Patient360TreatmentJourneyCourseWire {
  readonly courseId: number;
  readonly courseStatus: string;
  readonly visitCount: number;
  readonly acceptedItemCount: number;
  /** null = completion evidence unavailable (pre-20260717_1), never 0. */
  readonly completedItemCount: number | null;
  readonly remainingPlannedCount: number | null;
  readonly collectedMinorUnits: number;
  readonly ledgerState:
    | 'outstanding'
    | 'not_configured'
    | 'settled'
    | 'no_accepted_plan'
    | 'not_closed';
  readonly outstandingMinorUnits: number | null;
}

export interface Patient360TreatmentJourneyWire {
  readonly availability: 'available' | 'unavailable';
  readonly completionAvailability: 'available' | 'unavailable';
  readonly courses: readonly Patient360TreatmentJourneyCourseWire[];
}

/** Journey ledger-state labels. The 360's forbidden-balance vocabulary is
 * honored: the course roll-up renders as the PLAN REMAINDER (this
 * package's own money law), never "Outstanding", never a balance claim. */
export const JOURNEY_LEDGER_STATE_LABELS: Readonly<Record<string, string>> = {
  outstanding: 'Plan remainder due',
  not_configured: 'Accepted amount not configured',
  settled: 'Plan settled',
  no_accepted_plan: 'No accepted items yet',
  not_closed: 'Accepted items await their visit close',
};

/**
 * PATIENT-360-FOUR-FLAG-CORRECTIVE-V1 FLAG 1 + FLAG 4.
 *
 * ONE derivable money line per course, read STRAIGHT off the EXISTING
 * course roll-up (`ledgerState` + `collectedMinorUnits` +
 * `outstandingMinorUnits`). This module adds NO formula, NO summing across
 * courses, NO fallback from estimates, and NO fetch -- it only decides
 * WHETHER the remainder is derivable and hands the caller the same numbers
 * the journey card was already rendering.
 *
 * FLAG 4 (binding): these lines belong to the MONEY region. They are never
 * folded into the activity strip -- the current-state/history split and the
 * per-region role fencing depend on the money SUMMARY staying its own
 * region.
 */
export interface PatientPlanRemainderLine {
  readonly courseId: number;
  readonly ledgerLabel: string;
  readonly collectedMinorUnits: number;
  readonly outstandingMinorUnits: number | null;
}

/**
 * The derivable remainder lines, or NULL when the remainder genuinely
 * cannot be derived from the existing contract for this lens (no journey
 * lane on the wire, the lane is unavailable, no courses, or not a single
 * course carries a remainder figure). NULL is the caller's signal to keep
 * the honest PAYMENT_BALANCE_COPY -- an absent remainder is never rendered
 * as zero, and zero is never rendered as absent.
 */
export function derivePlanRemainderLines(
  summary: Patient360SummaryResponse,
): readonly PatientPlanRemainderLine[] | null {
  // PATIENT-360-DESIGN-CONSISTENCY-V1: ONE derivation, two shapes. The
  // course-money builder below is now THE single place the course roll-up
  // is read; this function stays the banked FLAG-1 projection of it, so a
  // second money derivation cannot appear behind the two call sites.
  const lines = buildCourseMoneyLines(summary);
  if (lines === null) return null;
  // DERIVABLE means the REMAINDER itself is known. A course whose
  // outstanding figure is null carries no remainder truth, so a lane made
  // only of those stays honestly unavailable.
  if (!lines.some((l) => l.outstandingMinorUnits !== null)) return null;
  return lines.map((l) => ({
    courseId: l.courseId,
    ledgerLabel: l.ledgerLabel,
    collectedMinorUnits: l.collectedMinorUnits,
    outstandingMinorUnits: l.outstandingMinorUnits,
  }));
}

export const JOURNEY_UNAVAILABLE_COPY = 'Treatment journey unavailable';
export const JOURNEY_COMPLETION_UNAVAILABLE_COPY =
  'Completion evidence unavailable';
export const JOURNEY_NO_COURSES_COPY =
  'No multi-visit treatment journey recorded.';

export interface Patient360SummaryResponse {
  readonly patientId: string;
  readonly umr: string;
  readonly patientStatus: string;
  readonly todayAppointments: readonly Patient360AppointmentWire[];
  readonly todayFocus: Patient360AppointmentWire | null;
  readonly recentVisits: readonly Patient360AppointmentWire[];
  readonly recentVisitsCapped: boolean;
  /** RETURNING-ELIGIBILITY-AUTHORITY-V1: the server's authoritative returning
   *  fact. Optional because an older server does not send it; `null` means the
   *  server could not derive it and the surface must degrade rather than call
   *  the person new. NEVER re-derive this from recentVisits -- that list is
   *  capped at ten and answering from it produced the false negative this
   *  field exists to remove. */
  readonly priorAttendance?: 'new' | 'returning' | null;
  readonly upcomingAppointments: readonly Patient360AppointmentWire[];
  readonly selectedAppointment?: Patient360AppointmentWire | null;
  readonly selectedAppointmentHistory?: {
    readonly availability: 'available' | 'unavailable';
    readonly items: readonly {readonly eventType: string; readonly occurredAt: string; readonly actorRole: string | null; readonly actorName?: string | null; readonly previousAppointmentTime?: string | null; readonly newAppointmentTime?: string | null; readonly cancelReasonCode?: string | null; readonly previousServiceName?: string | null; readonly newServiceName?: string | null; readonly previousDoctorName?: string | null; readonly newDoctorName?: string | null; readonly clinicRebookedToAppointmentId?: number | null; readonly changeReasonCode?: string | null}[];
    readonly capped: boolean;
  };
  readonly directlyLinkedAppointmentCount: number;
  readonly leadLinkedAppointmentCount: number;
  readonly intakeSpineAvailability: 'available' | 'unavailable';
  readonly payments: {
    readonly state: PaymentEvidenceState;
    readonly items: readonly Patient360PaymentItemWire[];
    readonly balance: 'not_available';
  };
  /** The VOIDED-payment history lane (#273 / FND-01): current-vs-history
   * split. OPTIONAL so older wire payloads keep rendering (additive
   * contract); absent renders as "no voided history". */
  readonly paymentHistory?: Patient360PaymentHistoryWire;
  readonly followUp: {
    readonly state: FollowUpEvidenceState;
    readonly scheduledFor: string | null;
  };
  /** Read-only treatment/checkout summary (TREATMENT-ESTIMATE-ACCEPTANCE
   * spine): counts + whole-INR sums ONLY -- no procedure names, no
   * clinical narrative, no computed balance. OPTIONAL so older wire
   * payloads keep rendering (additive contract). */
  readonly treatmentCheckout?: Patient360TreatmentCheckoutWire;
  /** Multi-visit journey lane (V6-P4). OPTIONAL so older wire payloads
   * keep rendering (additive contract); absent = lane not on this wire. */
  readonly treatmentJourney?: Patient360TreatmentJourneyWire;
  readonly identityReview: 'pending' | 'none' | 'unavailable';
  readonly externalClinicalRecord: 'not_connected';
  readonly primaryAction: {
    readonly action: Patient360PrimaryActionToken;
    readonly reason: string;
  };
  readonly journey: ReadonlyArray<{
    readonly key: PatientJourneySegmentKey;
    readonly state: PatientJourneySegmentState;
  }> | null;
  readonly journeyBasis: 'today' | 'recent_visit' | 'none';
  /** PATIENT-360-V1 (founder ruling D-360e, 2026-08-16): the patient-
   * narrowed D1 next-visit intent (the desk "To schedule" owner, ONE
   * patient). OPTIONAL so older wire payloads keep rendering (additive
   * contract); null/absent = a booking exists, no intent, or fault. */
  readonly nextVisitIntent?: PatientNextVisitIntentWire | null;
}

/** Mirror of the backend PatientNextVisitIntent (work-actions.ts) -- the
 * PHI-free patient-page projection: option token/label + the treatment
 * doctor's roster NAME + the finalize timestamp. */
export interface PatientNextVisitIntentWire {
  readonly returnRequest?: ReturnSchedulingRequestView;
  readonly optionKey: string;
  readonly optionLabel: string | null;
  readonly treatmentDoctorName: string | null;
  readonly closedAt: string | null;
}

// --- Identifier alias classification (package section 1) -------------------

/** The identifier systems that are INTERNAL to AdoraCare and therefore
 * never render as external MR aliases: 'adoracare_umr' IS the primary
 * identity (already the header's patient number); 'lead_id' is the
 * patient->lead journey bridge (it powers the timeline, not identity). */
export const INTERNAL_IDENTIFIER_SYSTEMS: readonly string[] = [
  'adoracare_umr',
  'lead_id',
];

/** Real-source labels for the known systems (schema CHECK vocabulary);
 * unknown systems fall back to the raw token (never guessed prose). */
const ALIAS_SYSTEM_LABELS: Readonly<Record<string, string>> = {
  ehnote_umr: 'EHNote UMR',
  ehnote_mr: 'EHNote MR',
  office24x7_contact: 'Office24x7 contact',
  other: 'Other external ID',
};

/** Systems owned by an external vendor/EHR (labeled "external" in the UI;
 * never presented as the primary AdoraCare patient number). */
const EXTERNAL_VENDOR_SYSTEMS: readonly string[] = [
  'ehnote_umr',
  'ehnote_mr',
  'office24x7_contact',
];

export interface PatientAliasView {
  readonly system: string;
  readonly label: string;
  readonly value: string;
  /** True for vendor/EHR-owned systems (EHNote, Office24x7, ...). */
  readonly external: boolean;
}

export const NO_EXTERNAL_MR_COPY = 'No external MR on file';

/**
 * Classify the record read's identifiers into the alias strip: internal
 * bridge systems are dropped, duplicates (same system+value) collapse to
 * one chip, every alias carries its real source label. Deterministic
 * order: by system then value.
 */
export function classifyPatientAliases(
  identifiers: ReadonlyArray<{ readonly system: string; readonly value: string }>,
): readonly PatientAliasView[] {
  const seen = new Set<string>();
  const aliases: PatientAliasView[] = [];
  for (const id of identifiers) {
    const system = id.system.trim();
    const value = id.value.trim();
    if (system.length === 0 || value.length === 0) continue;
    if (INTERNAL_IDENTIFIER_SYSTEMS.includes(system)) continue;
    const key = `${system}:${value}`;
    if (seen.has(key)) continue;
    seen.add(key);
    aliases.push({
      system,
      label: ALIAS_SYSTEM_LABELS[system] ?? system,
      value,
      external: EXTERNAL_VENDOR_SYSTEMS.includes(system) || system === 'other',
    });
  }
  return aliases.sort(
    (a, b) => a.system.localeCompare(b.system) || a.value.localeCompare(b.value),
  );
}

// --- Label maps (fields, not sentences) -------------------------------------

export const APPOINTMENT_STATUS_LABELS: Readonly<Record<string, string>> = {
  waitlist: 'Waitlisted',
  booked: 'Booked',
  confirmed: 'Confirmed',
  rescheduled: 'Rescheduled',
  arrived: 'Arrived',
  checked_in: 'Checked in',
  fulfilled: 'Visit complete',
  completed: 'Visit complete',
  cancelled: 'Cancelled',
  no_show: 'No show',
};

export function appointmentStatusLabel(status: string): string {
  // RECEPTION-OS-BUILD-V1 (F-09): delegate to THE ONE StatusChip canon
  // (ui/status-canon.ts). The local APPOINTMENT_STATUS_LABELS copy above was
  // the canon's golden SOURCE (WALK 21); adoption retires the duplication --
  // the constant stays exported for its remaining direct importers and is
  // lockstep-pinned equal to the canon, so the two can never drift.
  return statusChipLabel('appointment', status);
}

export const INTAKE_STATUS_LABELS: Readonly<
  Record<'none' | 'in_progress' | 'completed', string>
> = {
  none: 'Not started',
  in_progress: 'In progress',
  completed: 'Complete',
};

export const INTAKE_UNAVAILABLE_LABEL = 'Status unavailable';

export function intakeEvidenceLabel(intake: IntakeEvidenceWire): string {
  if (intake.availability === 'unavailable') return INTAKE_UNAVAILABLE_LABEL;
  const base = INTAKE_STATUS_LABELS[intake.status];
  return intake.mode !== null ? `${base} (${intake.mode})` : base;
}

/** Role-safe clinical ownership copy (package section 5): operational
 * state ONLY -- never clinical content. */
export const CLINICAL_STATE_LABELS: Readonly<
  Record<ClinicalOperationalState, string>
> = {
  not_started: 'Consultation not started',
  in_progress: 'Consultation in progress',
  completed: 'Clinical record completed',
  unavailable: 'Clinical status unavailable',
};

// --- THE DOCTOR / QUEUE STATE CONTRACT (F-2, corrective) --------------------

/**
 * PATIENT-360-AND-JOURNEY-TRUTH-CORRECTIVE-V1 (F-2): THE ONE deterministic
 * doctor/queue evidence-precedence contract. The founder saw "Doctor: Not
 * assigned" beside "In doctor queue" / "Ready for doctor" -- three strings
 * derived independently from the SAME status===checked_in fact while the
 * doctor field is perpetually null (doctor_id is unwired today).
 *
 * This is the ONLY place any surface derives a doctor/queue readiness label,
 * so the contradiction cannot recur. Queue membership is the existing
 * day-board derivation (checked_in == in the doctor queue). A doctor is
 * "assigned" ONLY when a roster name resolves -- so the queue label itself
 * states the assignment truth, and readiness ("Ready for Dr. X") is NEVER
 * emitted from check-in alone. Never implies assignment or readiness the
 * evidence does not prove.
 */
export type DoctorQueueState =
  | 'not_in_queue'
  | 'assigned_pre_queue'
  | 'awaiting_doctor_assignment'
  | 'in_queue_unassigned'
  | 'ready_for_doctor'
  | 'consult_in_progress'
  | 'consult_completed'
  | 'case_sheet_owed'
  | 'not_applicable'
  | 'unavailable';

export interface DoctorQueueInputs {
  /** The FHIR canonical projection (never the raw stored token); null = unknown. */
  readonly canonicalStatus: string | null;
  /** Arrival truth (patient_arrived event); null = no recorded arrival. */
  readonly arrivedAt: string | null;
  /** A doctor roster name resolved for this appointment (never a bare id). */
  readonly hasDoctorAssigned: boolean;
  readonly consultStarted: boolean;
  readonly clinicalState: ClinicalOperationalState;
  /** AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F8, derive-don't-store): TRUE when
   *  the identity-or-registration start gate is OPEN for this row (a patient
   *  record OR a complete lead registration -- the SAME rule as the doctor's
   *  Start door and the server's consult gate). With D4 an ARRIVED row whose
   *  gate is open already waits on the DOCTOR, so it derives the queue-
   *  speaking states. ABSENT/undefined = unknown -> the pre-D4 behavior
   *  (arrived stays pre-queue), so non-board callers are unchanged. */
  readonly startGateOpen?: boolean;
}

/**
 * Derive EXACTLY ONE doctor/queue state (deterministic first-match). A
 * consult signal on a non-visit (cancelled/no_show/waitlist) is contradictory
 * evidence and degrades honestly; an unknown status is 'unavailable'.
 */
export function deriveDoctorQueueState(
  input: DoctorQueueInputs,
): DoctorQueueState {
  const { canonicalStatus, arrivedAt, hasDoctorAssigned } = input;
  const consultUnderway =
    input.consultStarted || input.clinicalState === 'in_progress';
  const consultDone = input.clinicalState === 'completed';

  if (canonicalStatus === null) return 'unavailable';

  if (
    canonicalStatus === 'cancelled' ||
    canonicalStatus === 'no_show' ||
    canonicalStatus === 'waitlist'
  ) {
    // A consult signal on a visit that never happened is contradictory.
    return consultUnderway || consultDone ? 'unavailable' : 'not_applicable';
  }

  if (consultDone) return 'consult_completed';
  if (consultUnderway) return 'consult_in_progress';
  // Administrative fulfilment does not prove that a clinician completed a consult.
  if (canonicalStatus === 'fulfilled') return input.clinicalState === 'unavailable' ? 'unavailable' : 'case_sheet_owed';

  if (canonicalStatus === 'checked_in') {
    return hasDoctorAssigned ? 'ready_for_doctor' : 'in_queue_unassigned';
  }

  const arrived = canonicalStatus === 'arrived' || arrivedAt !== null;
  if (arrived) {
    // F8 (AUG-12-WALK-2-REGISTRATION-TRUTH-V1, derive-don't-store): an
    // ARRIVED row whose start gate is OPEN (registered or linked -- the D4
    // startable truth) is IN the doctor's queue already; readiness still
    // requires the resolved assignment (the F-2 hard rule stands).
    if (input.startGateOpen === true) {
      return hasDoctorAssigned ? 'ready_for_doctor' : 'in_queue_unassigned';
    }
    return hasDoctorAssigned
      ? 'assigned_pre_queue'
      : 'awaiting_doctor_assignment';
  }

  // booked / confirmed / rescheduled, pre-arrival.
  return hasDoctorAssigned ? 'assigned_pre_queue' : 'not_in_queue';
}

/**
 * Render the doctor/queue state as a single lawful label. Doctor-named
 * states interpolate the resolved roster name; a state that proves no
 * assignment NEVER names a doctor (F-2 hard rule). ASCII-clean.
 */
export function doctorQueueLabel(
  state: DoctorQueueState,
  doctorName: string | null,
): string {
  switch (state) {
    case 'not_in_queue':
      return 'Not in queue';
    case 'assigned_pre_queue':
      return doctorName !== null
        ? `Assigned to ${doctorName}`
        : 'Assigned to a doctor';
    case 'awaiting_doctor_assignment':
      return 'Checked in - awaiting doctor assignment';
    case 'in_queue_unassigned':
      return 'In doctor queue - awaiting assignment';
    case 'ready_for_doctor':
      return doctorName !== null ? `Ready for ${doctorName}` : 'Ready for doctor';
    case 'consult_in_progress':
      return 'Consult in progress';
    case 'consult_completed':
      return 'Consult completed';
    case 'case_sheet_owed':
      return 'Case sheet owed';
    case 'not_applicable':
      return 'Not in queue';
    case 'unavailable':
      return 'Status unavailable - conflicting operational evidence';
  }
}

/** Convenience: the doctor/queue label straight from an appointment wire
 *  (the ONE call site pattern the record page + any queue surface use). */
export function appointmentDoctorQueueLabel(
  a: Patient360AppointmentWire,
): string {
  return doctorQueueLabel(
    deriveDoctorQueueState({
      canonicalStatus: a.canonicalStatus,
      arrivedAt: a.arrivedAt,
      hasDoctorAssigned: a.doctorName !== null,
      consultStarted: a.consultStarted,
      clinicalState: a.clinicalState,
    }),
    a.doctorName,
  );
}

/** The honest label for a pre-attribution visit -- a consult act with NO
 *  recorded actor (never backfilled). VISIT-ACTOR-ATTRIBUTION-V1. */
/**
 * PATIENT-360-FOUR-FLAG-CORRECTIVE-V1 FLAG 2 (copy only): the honest
 * no-actor state reads as a NEUTRAL pending fact, never as a judgement on
 * the visit. The DERIVATION is untouched -- this constant's value changed,
 * nothing else.
 */
export const CONSULT_UNATTRIBUTED_LABEL = 'Doctor attribution: pending';

/**
 * VISIT-ACTOR-ATTRIBUTION-V1 (#277 / FND-14): the consult attribution line for
 * an appointment card. Returns a value ONLY when a consult ACT occurred (the
 * queue state is consult in-progress / completed -- so a cancelled/no-show
 * visit with a contradictory signal shows nothing): the resolved actor label
 * when one was recorded, else the honest CONSULT_UNATTRIBUTED_LABEL. Never
 * names a doctor the evidence does not prove; never backfills. Returns null
 * when no consult act happened (the line is not rendered).
 */
export function consultAttributionLabel(
  a: Patient360AppointmentWire,
): string | null {
  const state = deriveDoctorQueueState({
    canonicalStatus: a.canonicalStatus,
    arrivedAt: a.arrivedAt,
    hasDoctorAssigned: a.doctorName !== null,
    consultStarted: a.consultStarted,
    clinicalState: a.clinicalState,
  });
  if (state !== 'consult_in_progress' && state !== 'consult_completed') {
    return null;
  }
  return a.consultActor !== null
    ? a.consultActor.label
    : CONSULT_UNATTRIBUTED_LABEL;
}

/** TRUE when a consult act occurred but no actor was recorded (FLAG 2). */
export function consultAttributionPending(
  a: Patient360AppointmentWire,
): boolean {
  return consultAttributionLabel(a) === CONSULT_UNATTRIBUTED_LABEL;
}

/**
 * The FULLY COMPOSED attribution fact for a row-style surface (the activity
 * lane). Attributed rows keep the banked "Consult by X" phrasing; the
 * no-actor state renders the neutral pending copy STANDALONE, so the strip
 * never reads "Consult by Doctor attribution: pending" (FLAG 2). Returns
 * null when no consult act occurred -- the fact simply does not render.
 */
export function consultAttributionFact(
  a: Patient360AppointmentWire,
): string | null {
  const label = consultAttributionLabel(a);
  if (label === null) return null;
  return label === CONSULT_UNATTRIBUTED_LABEL
    ? CONSULT_UNATTRIBUTED_LABEL
    : `Consult by ${label}`;
}

export const JOURNEY_SEGMENT_LABELS: Readonly<
  Record<PatientJourneySegmentKey, string>
> = {
  booked: 'Booked',
  arrived: 'Arrived',
  intake: 'Intake',
  checked_in: 'Checked in',
  ready_for_doctor: 'Ready for doctor',
  consultation: 'Consultation',
  checkout: 'Checkout',
  follow_up: 'Follow-up',
};

export const JOURNEY_STATE_LABELS: Readonly<
  Record<PatientJourneySegmentState, string>
> = {
  complete: 'Done',
  current: 'Now',
  pending: 'Pending',
  not_evidenced: 'Not evidenced',
  evidenced: 'Evidence recorded',
  unavailable: 'Unavailable',
  not_applicable: 'Not applicable',
};

export const FOLLOW_UP_STATE_LABELS: Readonly<
  Record<FollowUpEvidenceState, string>
> = {
  required: 'Follow-up required',
  scheduled: 'Follow-up scheduled',
  completed: 'Completed',
  none: 'No follow-up evidence',
  unavailable: 'Status unavailable',
};

/** Payment honesty vocabulary (package section 6) -- the ONLY payment
 * sentences this surface may utter. No balance is ever computed. */
export const PAYMENT_STATE_LABELS: Readonly<Record<PaymentEvidenceState, string>> = {
  recorded: 'Payment recorded',
  multiple_recorded: 'Multiple payments recorded',
  none_recorded: 'No payment recorded',
  unavailable: 'Payment status unavailable',
};

/** Treatment/checkout summary honesty vocabulary (TREATMENT-ESTIMATE-
 * ACCEPTANCE spine): counts + sums only; unknown prices named, never
 * fabricated; the account line stays PAYMENT_BALANCE_COPY. */
export const TREATMENT_SUMMARY_UNAVAILABLE_COPY =
  'Treatment summary unavailable';
export const TREATMENT_PRICE_UNAVAILABLE_COPY = 'Price unavailable';
export const TREATMENT_NO_ITEMS_COPY = 'No treatment recommended yet';
export const TREATMENT_DISCOUNT_STATE_LABELS: Readonly<Record<string, string>> = {
  none: 'No discount applied',
  applied: 'Discount applied',
  unavailable: 'Discount state unavailable',
};

/** THE CONVERSION VOCABULARY labels (corrective section F -- the ALLOWED
 * list verbatim; the checkout panel carries the same map). Converted
 * labels render ONLY when the backend derived them from active non-voided
 * first-payment evidence. The forbidden vocabulary ("Fully paid", "Zero
 * balance", "Balance due", "Outstanding balance", "Account settled",
 * "Final invoice", "Treatment completed") never appears here. */
export const CONVERSION_STATE_LABELS: Readonly<
  Record<CommercialConversionStateWire, string>
> = {
  no_treatment_recommended: 'No treatment recommended',
  undecided: 'Undecided',
  accepted_payment_pending: 'Accepted - payment pending',
  partially_accepted_payment_pending: 'Partially accepted - payment pending',
  declined: 'Declined',
  deferred: 'Deferred',
  converted_first_payment: 'First payment received - Converted',
  converted_chief_complaint: 'Converted - Chief Complaint',
  converted_full_package: 'Converted - Full Package',
  unavailable: 'Treatment status unavailable',
};

/** Total label helper: absent field (pre-corrective payload) renders the
 * honest unavailable copy, never a guess. */
export function conversionStateLabel(
  token: CommercialConversionStateWire | undefined,
): string {
  return CONVERSION_STATE_LABELS[token ?? 'unavailable'];
}

export const PAYMENT_BALANCE_COPY = 'Account balance not available';
export const PAYMENT_UNLINKED_COPY = 'Recorded without appointment linkage';

/** EHNote coexistence honesty (package section 8) -- fixed truthful copy;
 * never a synchronization claim. */
export const EXTERNAL_RECORD_COPY = 'External clinical record not connected';
export const EXTERNAL_STATUS_COPY = 'EHNote status unavailable';
export const EXTERNAL_PROVENANCE_COPY =
  'External clinical, treatment, and billing data is not synchronized into AdoraCare.';

export const NO_APPOINTMENT_TODAY_COPY = 'No appointment found for today';

// --- Primary action rendering ------------------------------------------------

export interface PrimaryActionView {
  readonly label: string;
  /** Link into the EXISTING lawful flow; null renders a state chip, not a
   * button (render-only orchestration; zero new writers). */
  readonly href: string | null;
}

export const ACTION_REASON_COPY: Readonly<Record<string, string>> = {
  identity_review_pending:
    'A possible duplicate record needs review before further front-desk action.',
  no_appointment_today: 'No appointment today; nothing is waiting on the desk.',
  awaiting_arrival: 'Booked; the patient has not been marked arrived yet.',
  awaiting_intake: 'Arrived; intake has not been started.',
  intake_in_progress: 'Intake is in progress.',
  ready_to_check_in: 'Intake is complete; check-in has not landed yet.',
  arrived_check_in_next: 'Arrived; check-in is the next step.',
  awaiting_doctor: 'Checked in and waiting for the doctor.',
  consultation_in_progress: 'The consultation is in progress.',
  awaiting_checkout: 'The visit is complete; no payment evidence is recorded yet.',
  visit_complete: 'The visit is complete.',
  follow_up_required: 'An open follow-up item needs scheduling.',
  appointment_cancelled: "Today's appointment was cancelled.",
  appointment_no_show: "Today's appointment was marked no-show.",
  waitlisted: 'The request is waitlisted; no slot is booked yet.',
  status_unavailable: 'The appointment state could not be determined.',
  role_view_only: 'Your role views this record; the acting role owns the next step.',
};

/**
 * Map the derived action token to its label + the EXISTING lawful door:
 * front-desk acts live on the walk-in day board (/walkin -- the ONE
 * arrival/intake/check-in surface); checkout is the billing-entry door
 * (URL-prefill carrier, the recordPaymentHref idiom); review opens the
 * merge queue for the merge audience (null for others -- state chip only).
 */
export function primaryActionView(
  action: Patient360PrimaryActionToken,
  args: {
    readonly patientId: string;
    readonly focusClinicId: number | null;
    readonly focusAppointmentId: number | null;
    readonly canOpenMergeQueue: boolean;
  },
): PrimaryActionView {
  switch (action) {
    case 'review_required':
      return {
        label: 'Review required',
        href: args.canOpenMergeQueue ? '/dashboard/patients/merge-queue' : null,
      };
    case 'mark_arrived':
      return { label: 'Mark arrived', href: '/walkin' };
    case 'start_intake':
      return { label: 'Start intake', href: '/walkin' };
    case 'continue_intake':
      return { label: 'Continue intake', href: '/walkin' };
    case 'check_in':
      return { label: 'Check in', href: '/walkin' };
    case 'record_payment': {
      const params = new URLSearchParams();
      params.set('patientId', args.patientId);
      if (args.focusClinicId !== null) {
        params.set('clinicId', String(args.focusClinicId));
      }
      if (args.focusAppointmentId !== null) {
        params.set('appointmentId', String(args.focusAppointmentId));
      }
      return {
        label: 'Record payment',
        href: `/dashboard/billing-entry?${params.toString()}`,
      };
    }
    case 'schedule_follow_up':
      return { label: 'Schedule follow-up', href: '/walkin' };
    case 'no_action_required':
      return { label: NO_ACTION_REQUIRED_LABEL, href: null };
  }
}

// --- Today's-desk action vs PATIENT next action (F1 / #290) ------------------

/**
 * CORE-DEMO-JOURNEY-STITCH-V1 (F1 / FND-26 #290): the desk-scoped "No action
 * required" copy (today's desk has nothing waiting) and the honest desk copy
 * for when the desk is idle but the PATIENT still has outstanding
 * continuity/commercial work -- the two must never be conflated (an
 * unfinished journey is never "nothing to do").
 */
export const NO_ACTION_REQUIRED_LABEL = 'No action required';
export const DESK_IDLE_WITH_OUTSTANDING_LABEL = 'No desk action right now';

/** The reason copy the patient-next-action band may utter (continuity +
 *  commercial only; every line maps to existing wire evidence). */
export const PATIENT_NEXT_ACTION_PAYMENT_LABEL = 'Collect accepted-treatment payment';
export const PATIENT_NEXT_ACTION_PAYMENT_REASON =
  'Treatment was accepted; the payment for it is still pending.';
export const PATIENT_NEXT_ACTION_FOLLOW_UP_REASON =
  'The visit is complete; a follow-up still needs to be scheduled.';

/**
 * TRUE when the patient's journey carries outstanding CONTINUITY or COMMERCIAL
 * work derived ONLY from existing wire evidence -- a follow-up is required, or
 * accepted treatment is still awaiting its payment. While this is true the
 * desk's "No action required" MUST NOT render (F1 / #290: today's idle desk is
 * NOT the same fact as a finished patient journey). Invents no business state.
 */
export function journeyHasOutstandingWork(
  summary: Patient360SummaryResponse,
): boolean {
  if (summary.followUp.state === 'required') return true;
  const tc = summary.treatmentCheckout;
  return (
    tc !== undefined &&
    tc.availability === 'available' &&
    (tc.commercialConversionState === 'accepted_payment_pending' ||
      tc.commercialConversionState === 'partially_accepted_payment_pending')
  );
}

export interface PatientNextActionView {
  /** Controlled continuity/commercial label (never free prose). */
  readonly label: string;
  /** Why the work is outstanding. */
  readonly reason: string;
  readonly tone: AcBadgeTone;
  /** A lawful door when one exists; null renders a state chip (no dead link). */
  readonly href: string | null;
}

/**
 * The PATIENT next action -- the continuity + commercial next step, DISTINCT
 * from today's desk action. Derived ONLY from existing wire evidence
 * (commercial conversion state, follow-up state); never a new business state.
 * Precedence: OPEN MONEY FIRST (accepted treatment awaiting collection) then
 * CONTINUITY (a required follow-up). Returns null when the journey has no
 * outstanding continuity/commercial work (the desk action is then the whole
 * truth). The payment leg reuses the EXISTING billing-entry door (the
 * record_payment idiom); the follow-up leg has no dedicated route yet, so it
 * renders as an honest state chip, never a fabricated link.
 */
export function buildPatientNextAction(
  summary: Patient360SummaryResponse,
  args: {
    readonly patientId: string;
    readonly focusClinicId: number | null;
    readonly focusAppointmentId: number | null;
  },
): PatientNextActionView | null {
  const tc = summary.treatmentCheckout;
  const paymentPending =
    tc !== undefined &&
    tc.availability === 'available' &&
    (tc.commercialConversionState === 'accepted_payment_pending' ||
      tc.commercialConversionState === 'partially_accepted_payment_pending');
  if (paymentPending) {
    const door = primaryActionView('record_payment', {
      patientId: args.patientId,
      focusClinicId: args.focusClinicId,
      focusAppointmentId: args.focusAppointmentId,
      canOpenMergeQueue: false,
    });
    return {
      label: PATIENT_NEXT_ACTION_PAYMENT_LABEL,
      reason: PATIENT_NEXT_ACTION_PAYMENT_REASON,
      tone: 'pending',
      href: door.href,
    };
  }
  if (summary.followUp.state === 'required') {
    return {
      label: FOLLOW_UP_STATE_LABELS.required,
      reason: PATIENT_NEXT_ACTION_FOLLOW_UP_REASON,
      tone: 'info',
      href: null,
    };
  }
  return null;
}

/**
 * The label the TODAY'S-DESK-ACTION chip shows: the desk action's own label,
 * EXCEPT that a `no_action_required` desk state on an unfinished journey never
 * reads "No action required" (F1 / #290) -- it reads the honest idle-desk copy
 * while the patient-next-action band carries the outstanding work.
 */
export function deskActionLabel(
  summary: Patient360SummaryResponse,
  action: PrimaryActionView,
): string {
  if (
    summary.primaryAction.action === 'no_action_required' &&
    journeyHasOutstandingWork(summary)
  ) {
    return DESK_IDLE_WITH_OUTSTANDING_LABEL;
  }
  return action.label;
}

// --- Waiting duration (deterministic only) -----------------------------------

/**
 * Whole minutes waited since the recorded arrival, or null when not
 * deterministically computable (no arrival evidence / bad timestamp /
 * negative). Callers inject nowMs; render zone-labeled.
 */
export function waitingMinutes(
  arrivedAt: string | null,
  nowMs: number,
): number | null {
  if (arrivedAt === null) return null;
  const t = Date.parse(arrivedAt);
  if (Number.isNaN(t)) return null;
  const minutes = Math.floor((nowMs - t) / 60000);
  return minutes >= 0 ? minutes : null;
}

// --- Money rendering (typed minor units; REUSE the canonical formatter) ------

/** Format a payment evidence amount. REUSES the ONE minor-units formatter
 * (billing-entry-view.ts) for INR -- never a second money renderer; a
 * non-INR currency (future US tenant) renders with its explicit code. */
export function formatPaymentAmount(
  amountMinorUnits: number,
  currency: string,
): string {
  if (currency === 'INR') return formatMinorUnitsInr(amountMinorUnits);
  const units = Math.trunc(amountMinorUnits / 100);
  const cents = Math.abs(amountMinorUnits % 100);
  const base = `${currency} ${units.toLocaleString('en-IN')}`;
  return cents === 0 ? base : `${base}.${String(cents).padStart(2, '0')}`;
}

// --- Voided payment history vocabulary (#273 / FND-01) ----------------------

/** The closed payment-method vocabulary (mirrors patient_payments_method_chk /
 * lib/patient-payments.ts PAYMENT_METHODS). An unknown token renders verbatim
 * (never guessed prose). */
export const PAYMENT_METHOD_LABELS: Readonly<Record<string, string>> = {
  cash: 'Cash',
  upi: 'UPI',
  card: 'Card',
  insurance: 'Insurance',
  bank_transfer: 'Bank transfer',
  other: 'Other',
};

export function paymentMethodLabel(method: string | null): string {
  if (method === null || method === '') return 'Method not recorded';
  return PAYMENT_METHOD_LABELS[method] ?? method;
}

/** The closed void-reason vocabulary (mirrors patient_payments_void_reason_chk /
 * lib/patient-payments.ts PAYMENT_VOID_REASONS). Supervisor-tier: only the
 * billing-supervisor wire carries a void reason at all. */
export const PAYMENT_VOID_REASON_LABELS: Readonly<Record<string, string>> = {
  entry_error: 'Entry error',
  duplicate: 'Duplicate',
  other: 'Other',
};

export function paymentVoidReasonLabel(reason: string | null | undefined): string {
  if (reason === null || reason === undefined || reason === '') {
    return 'Reason not recorded';
  }
  return PAYMENT_VOID_REASON_LABELS[reason] ?? reason;
}

/** The single VOIDED status word (typed status, never prose). */
export const PAYMENT_VOIDED_STATUS_LABEL = 'Voided';

/** Payment-history honesty copy (current-vs-history split). NEVER a computed
 * balance; the voided rows are corrected history, never active collection. */
export const PAYMENT_HISTORY_NONE_COPY = 'No voided payments in history';
export const PAYMENT_HISTORY_UNAVAILABLE_COPY = 'Payment history unavailable';
export const PAYMENT_HISTORY_SUB_COPY =
  'Voided payments are kept for the audit trail and never count as collected.';

// --- Highlights panel (the five-second decision set; #278 / FND-17) ---------

/**
 * ONE highlight fact on the Patient 360 highlights panel: a labeled,
 * decision-relevant value with a status tone. The panel surfaces the
 * receptionist's five-second set -- who/why-today, doctor, intake/review,
 * treatment, and money -- so the flat all-cards-equal hierarchy (#278 /
 * FND-17) gives way to a scan-first band. Pure data: the page renders it with
 * the shared primitives (AcBadge tones + the highlights CSS).
 */
export interface PatientHighlight {
  /** Stable key (also the data-testid suffix). */
  readonly key: string;
  /** Uppercase micro-label (the FIELDS-NOT-SENTENCES idiom). */
  readonly label: string;
  /** The typed value word/phrase (a controlled label, never free prose). */
  readonly value: string;
  /** Semantic status tone (never color-only: the value word carries meaning). */
  readonly tone: AcBadgeTone;
  /** Money / current-visit highlights read FIRST (the emphasis tier). */
  readonly emphasis: boolean;
}

function visitHighlightTone(status: string): AcBadgeTone {
  switch (status) {
    case 'arrived':
    case 'checked_in':
      return 'brand';
    case 'fulfilled':
    case 'completed':
      return 'positive';
    case 'cancelled':
    case 'no_show':
      return 'danger';
    default:
      return 'info';
  }
}

function intakeHighlightTone(intake: IntakeEvidenceWire): AcBadgeTone {
  if (intake.availability === 'unavailable') return 'neutral';
  if (intake.status === 'completed') return 'positive';
  if (intake.status === 'in_progress') return 'pending';
  return 'neutral';
}

function paymentHighlightTone(state: PaymentEvidenceState): AcBadgeTone {
  return state === 'recorded' || state === 'multiple_recorded'
    ? 'positive'
    : 'neutral';
}

/** Exported for reuse by other canonical-conversion consumers (COC-RENDER-
 *  CLOSURE-V1, activity 20) -- the SAME tone-per-state mapping renders
 *  everywhere this vocabulary appears, so "Converted" is never a different
 *  colour on a different screen. */
export function treatmentHighlightTone(
  token: CommercialConversionStateWire | undefined,
): AcBadgeTone {
  switch (token) {
    case 'converted_first_payment':
    case 'converted_chief_complaint':
    case 'converted_full_package':
      return 'positive';
    case 'accepted_payment_pending':
    case 'partially_accepted_payment_pending':
      return 'info';
    case 'declined':
      return 'danger';
    case 'deferred':
    case 'undecided':
      return 'pending';
    default:
      return 'neutral';
  }
}

function doctorHighlightTone(a: Patient360AppointmentWire): AcBadgeTone {
  const state = deriveDoctorQueueState({
    canonicalStatus: a.canonicalStatus,
    arrivedAt: a.arrivedAt,
    hasDoctorAssigned: a.doctorName !== null,
    consultStarted: a.consultStarted,
    clinicalState: a.clinicalState,
  });
  switch (state) {
    case 'ready_for_doctor':
    case 'assigned_pre_queue':
    case 'consult_in_progress':
      return 'brand';
    case 'consult_completed':
      return 'positive';
    case 'case_sheet_owed':
      return 'pending';
    default:
      return 'neutral';
  }
}

/**
 * Build the highlights panel facts from the operational summary -- the
 * decision-relevant set surfaced at a glance (the 360 law's overview; #278 /
 * FND-17). Deterministic and honest: a fact renders ONLY when its evidence
 * exists (no fabricated "next appointment", no invented payment). Order is the
 * scan order: today visit, doctor, intake/review, treatment, payment.
 */
export function buildPatientHighlights(
  summary: Patient360SummaryResponse,
): readonly PatientHighlight[] {
  const highlights: PatientHighlight[] = [];
  const focus = summary.todayFocus;
  const upcoming = summary.upcomingAppointments[0] ?? null;

  // 1) TODAY VISIT -- the current operational position (why they are here).
  if (focus !== null) {
    highlights.push({
      key: 'visit',
      label: 'Today',
      value: appointmentStatusLabel(focus.status),
      tone: visitHighlightTone(focus.status),
      emphasis: true,
    });
  } else if (upcoming !== null) {
    highlights.push({
      key: 'visit',
      label: 'Next visit',
      value: 'Upcoming appointment',
      tone: 'info',
      emphasis: true,
    });
  } else {
    highlights.push({
      key: 'visit',
      label: 'Today',
      value: NO_APPOINTMENT_TODAY_COPY,
      tone: 'neutral',
      emphasis: false,
    });
  }

  // 2) DOCTOR / CONSULT attribution (only when a today visit is present).
  if (focus !== null) {
    highlights.push({
      key: 'doctor',
      label: 'Doctor',
      value: appointmentDoctorQueueLabel(focus),
      tone: doctorHighlightTone(focus),
      emphasis: false,
    });
  }

  // 3) INTAKE / REVIEW status -- identity review pending always wins.
  if (summary.identityReview === 'pending') {
    highlights.push({
      key: 'review',
      label: 'Review',
      value: 'Identity review pending',
      tone: 'danger',
      emphasis: true,
    });
  } else if (focus !== null) {
    highlights.push({
      key: 'intake',
      label: 'Intake',
      value: intakeEvidenceLabel(focus.intake),
      tone: intakeHighlightTone(focus.intake),
      emphasis: false,
    });
  }

  // 4) TREATMENT / acceptance state (only when treatment was recommended).
  const tc = summary.treatmentCheckout;
  if (
    tc !== undefined &&
    tc.availability === 'available' &&
    tc.recommendedItemCount > 0
  ) {
    highlights.push({
      key: 'treatment',
      label: 'Treatment',
      value: conversionStateLabel(tc.commercialConversionState),
      tone: treatmentHighlightTone(tc.commercialConversionState),
      emphasis: false,
    });
  }

  // 5) PAYMENT / outstanding state (money -- always shown, emphasis tier).
  highlights.push({
    key: 'payment',
    label: 'Payment',
    value: PAYMENT_STATE_LABELS[summary.payments.state],
    tone: paymentHighlightTone(summary.payments.state),
    emphasis: true,
  });

  return highlights;
}

// --- Activity lane (PATIENT-360-ENTERPRISE-FINISH-V1, D2) ------------------

/**
 * ONE chronological ACTIVITY entry, merged from the facts the summary read
 * ALREADY carries. No new fetch, no new fact, no derivation beyond ordering
 * and the existing label maps: a visit entry restates the visit row, a
 * payment entry restates the recorded payment, a void entry restates the
 * corrected-history row.
 *
 * CURRENT-VS-HISTORY (the banked vocabulary law): this lane is the VISIT
 * HISTORY side. The CURRENT side (today's focus card, the today/upcoming
 * appointment related list, the active collection lane, the treatment state)
 * renders in its own regions, so NO fact renders twice on the page.
 *
 * PAYMENT LANE FENCE: the payment current-vs-history split (the active
 * collection lane + the VOIDED corrected-history rows) is the structure
 * banked by #273 / FND-01 and it stays in the payments region, unmoved --
 * including the supervisor-tier fencing of `voidReason` / `voidedByLabel`
 * and every one of its negative-visibility pins. Folding the money lane into
 * this strip is left to the timeline-unification carrier (V7-P4 / gap.r-40)
 * and recorded as a deferral in the run record.
 *
 * ROLE SHAPE (LAW 3): `byLabel` exists ONLY when the wire carried an actor.
 * `consultAttributionLabel` returns null when no consult act occurred, so a
 * row without attribution renders NO provenance actor -- structural absence,
 * never a placeholder and never an invented actor.
 */
export type PatientActivityKind = 'visit';

export interface PatientActivityEntry {
  /** Stable row key (kind-scoped so ids never collide across lanes). */
  readonly id: string;
  readonly kind: PatientActivityKind;
  /** ISO instant used ONLY for the newest-first merge; the renderer owns
   *  the zone-labeled display string (this module stays pure/format-free). */
  readonly occurredAt: string;
  /** Row headline (the typed value, never a sentence). */
  readonly title: string;
  /** Labeled short facts, already resolved through the existing label maps. */
  readonly facts: readonly string[];
  /** Appointment status token for the row chip. */
  readonly appointmentStatus: string | null;
}

export const ACTIVITY_EMPTY_TITLE = 'No earlier visit evidence for this patient.';
export const ACTIVITY_EMPTY_BODY =
  'Past visits appear here as they are recorded.';

/**
 * PATIENT-360-FOUR-FLAG-CORRECTIVE-V1 FLAG 3: the muted intake state for a
 * HISTORY row. A past visit that carries no intake session is not a visit
 * with an unfinished task -- it is a visit from before intake was captured
 * on this surface -- so the history lane renders the repo's muted idiom
 * instead of the live "Not started" wording.
 *
 * SCOPE: the HISTORY lane only. The seam is the EXISTING current-vs-history
 * split already in the page (this builder feeds history; the Today card and
 * the appointments table feed current), NOT a date rule -- the wire carries
 * no per-visit "intake era" marker and inventing one would be a new rule.
 * Current and future visits keep live intake behavior byte-for-byte.
 */
export const INTAKE_HISTORICAL_MUTED_LABEL = '-';

/** The intake fact for a HISTORY row: live wording for every state the
 *  intake spine actually observed, the muted idiom for the never-started
 *  case. `availability: 'unavailable'` keeps its own honest copy. */
export function historicalIntakeLabel(intake: IntakeEvidenceWire): string {
  if (intake.availability === 'available' && intake.status === 'none') {
    return INTAKE_HISTORICAL_MUTED_LABEL;
  }
  return intakeEvidenceLabel(intake);
}

/** Newest-first by instant; ties fall back to the stable id so the merge is
 *  deterministic under identical timestamps (SSR pins depend on it). */
function byNewestFirst(
  a: PatientActivityEntry,
  b: PatientActivityEntry,
): number {
  const at = Date.parse(a.occurredAt);
  const bt = Date.parse(b.occurredAt);
  if (Number.isNaN(at) && Number.isNaN(bt)) return a.id.localeCompare(b.id);
  if (Number.isNaN(at)) return 1;
  if (Number.isNaN(bt)) return -1;
  if (bt !== at) return bt - at;
  return a.id.localeCompare(b.id);
}

/**
 * Build the ONE chronological activity strip from the operational summary.
 * Pure: same input, same output; renders nothing and formats nothing.
 */
/**
 * THE ONE wording for "where did THIS EPISODE happen".
 *
 * A patient at a 119-branch chain does not belong to a branch: they have ONE
 * registration fact and N episodes, each at a branch. So every clinic name on
 * this surface must say which question it answers -- `Registered at` for the
 * historical registration fact, `Visit at` for the branch an individual
 * episode happened at. Neither is "the patient's clinic", because there is no
 * such thing and no column for it (founder ruling, Phase 4A recon).
 */
export const VISIT_CLINIC_LABEL = 'Visit at';

/** All authorized summary partitions, deduplicated by appointment identity.
 * The explicitly selected snapshot takes precedence; no additional fetch or scope. */
export function patientSummaryVisits(summary: Patient360SummaryResponse): readonly Patient360AppointmentWire[] {
  return [...new Map([
    ...summary.recentVisits, ...summary.upcomingAppointments, ...summary.todayAppointments,
    ...(summary.todayFocus ? [summary.todayFocus] : []),
    ...(summary.selectedAppointment ? [summary.selectedAppointment] : []),
  ].map(visit => [visit.appointmentId, visit])).values()]
    .sort((a, b) => b.scheduledAt.localeCompare(a.scheduledAt) || b.appointmentId - a.appointmentId);
}

export function buildPatientActivity(
  summary: Patient360SummaryResponse,
): readonly PatientActivityEntry[] {
  const entries: PatientActivityEntry[] = [];

  // All saved visits, including today and the selected future appointment.
  for (const v of patientSummaryVisits(summary)) {
    const attribution = consultAttributionFact(v);
    const facts: string[] = [
      `${VISIT_CLINIC_LABEL} ${clinicLabelOf(v.clinicLabel)}`,
      // FLAG 3: the HISTORY lane's muted intake idiom (see
      // historicalIntakeLabel) -- a past visit with no intake session is not
      // an unfinished task.
      `Intake: ${historicalIntakeLabel(v.intake)}`,
      CLINICAL_STATE_LABELS[v.clinicalState],
      v.paymentRecorded ? 'Payment recorded' : 'No payment recorded',
    ];
    if (v.doctorName !== null) facts.splice(1, 0, v.doctorName);
    // VISIT-ACTOR-ATTRIBUTION-V1 (#277 / FND-14): WHO ran the consult, or the
    // neutral pending line -- rendered ONLY when a consult act occurred
    // (consultAttributionFact returns null otherwise, so a visit with no
    // consult carries NO attribution fact). FLAG 2 changed the no-actor
    // COPY only; the derivation is untouched.
    if (attribution !== null) facts.push(attribution);
    if (v.linkage !== 'direct') facts.push('Via lead - not directly linked');
    entries.push({
      id: `visit:${v.appointmentId}`,
      kind: 'visit',
      occurredAt: v.scheduledAt,
      title: appointmentStatusLabel(v.status),
      // Drop any fact that resolved to nothing: a blank chip is a blank hole,
      // and the honest-state law has no room for one.
      facts: facts.filter((f) => typeof f === 'string' && f.trim() !== ''),
      appointmentStatus: v.status,
    });
  }

  return entries.sort(byNewestFirst);
}

// --- PATIENT-360-V1 (founder MATCH 2026-08-16): the ONE-page vocabulary ----

/**
 * Plain-language labels for the consent_records vocabulary (D-360c + the
 * FR-2 wording fix): the schema CHECK's six types, one voice, sentence
 * case. An unknown token renders ITSELF (never an invented label -- the
 * follow-up optionLabel posture).
 */
export const CONSENT_TYPE_LABELS: Readonly<Record<string, string>> = {
  registration_privacy: 'Registration privacy',
  intake_acknowledgment: 'Intake acknowledgment',
  data_processing: 'Data processing',
  guardian_consent: 'Guardian consent',
  communication_whatsapp: 'WhatsApp communication',
  communication_sms: 'SMS communication',
};

export function consentTypeLabel(type: string): string {
  return CONSENT_TYPE_LABELS[type] ?? type;
}

export const CONSENTS_NONE_COPY = 'No consent records on file.';

// --- The next-visit band vocabulary (D-360e: booking -> intent -> none) ----

export const NEXT_VISIT_NONE_COPY = 'No follow-up set';

/**
 * The intent-state line: "To schedule - Review in 1 week - with
 * Dr. Rajeshwari - sent to Front Desk at close". Truth-first wording: the
 * treatment doctor is the WITH doctor (who treats next), never claimed as
 * the sender; "Next visit set" is NEVER claimed (the D1 authority truth).
 */
export function nextVisitIntentLine(
  intent: PatientNextVisitIntentWire,
): string {
  const parts: string[] = ['To schedule'];
  const option = intent.optionLabel ?? intent.optionKey;
  if (option.trim() !== '') parts.push(option);
  if (intent.returnRequest) {
    const requested = intent.returnRequest;
    parts.push(requested.requestedDoctorName ? `requested with ${requested.requestedDoctorName}` : requested.requestedDoctorId === null ? 'requested doctor not set' : 'requested doctor name unavailable');
    if (requested.durationMinutes !== null) parts.push(`${requested.durationMinutes} min requested`);
  } else if (intent.treatmentDoctorName !== null && intent.treatmentDoctorName.trim() !== '') {
    parts.push(`with ${intent.treatmentDoctorName}`);
  }
  parts.push('sent to Front Desk at close');
  return parts.join(' - ');
}

// --- CONTINUITY (LAKSHMI-2 ruling 2026-08-16) -- mirrored voice ------------

/** Mirror of lib/domain/clinical/visit-continuity.ts
 * CONTINUITY_INTAKE_ABSENT_COPY (the separate-packages convention): the
 * labeled honest absence BOTH consumers render for a null intakeFacts --
 * never blanks dressed as data (the honesty valve). */
export const CONTINUITY_INTAKE_ABSENT_COPY =
  'Previous intake not captured digitally for this visit.';

/** The next-planned line for the continuity snapshot (booking-else-intent-
 * else-none, the D-360e voice; datetime formatting stays the caller's ONE
 * IST owner). */
export const CONTINUITY_NO_NEXT_COPY = 'No follow-up set';

// --- PATIENT-360-DESIGN-CONSISTENCY-V1 (founder MATCH 2026-08-17) ----------
//
// The founder-MATCHED record-page composition, expressed as PURE selectors
// and honest-copy builders over the EXISTING owners. Nothing below fetches,
// mutates, or derives a NEW fact:
//   - the course ledger stays lib/billing-queue.ts deriveCourseLedger's
//     (this module only READS the roll-up the wire already carries);
//   - the visit/treatment states stay lib/domain/patient/patient-360.ts's;
//   - the money vocabulary stays JOURNEY_LEDGER_STATE_LABELS.
// Presentation only -- no writer, no read widening, no policy decision.

// --- FOUR KEY FACTS (target hierarchy rung 3) ------------------------------

/** The ruled key-fact count (the founder target hierarchy: FOUR KEY FACTS). */
export const KEY_FACT_COUNT = 4;

/**
 * The ruled priority when the highlight builder offers more facts than the
 * four the hierarchy admits. HIGHEST FIRST:
 *   review    -- an identity-review flag is the one attention-shaped fact
 *                that must never be the one dropped;
 *   visit     -- why this patient is here / when they are next here;
 *   payment   -- the money state;
 *   treatment -- the acceptance state;
 *   intake / doctor -- both already render in full inside the Today region,
 *                so they are the lawful ones to fall out of the top four.
 * NOTHING IS HIDDEN BY THIS SELECTOR: every fact it drops renders in full
 * further down the same page. It chooses what reads FIRST, never what is
 * knowable.
 */
const KEY_FACT_PRIORITY: readonly string[] = [
  'review',
  'visit',
  'payment',
  'treatment',
  'intake',
  'doctor',
];

/**
 * The ruled FOUR key facts, in the BUILDER'S OWN reading order. Fewer than
 * four facts render as however many exist (never padded with a fabricated
 * fact); an unknown key sorts last but is never invented or renamed.
 */
export function selectKeyFacts(
  facts: readonly PatientHighlight[],
  limit: number = KEY_FACT_COUNT,
): readonly PatientHighlight[] {
  if (limit <= 0) return [];
  if (facts.length <= limit) return facts;
  const rank = (fact: PatientHighlight): number => {
    const at = KEY_FACT_PRIORITY.indexOf(fact.key);
    return at === -1 ? KEY_FACT_PRIORITY.length : at;
  };
  const kept = new Set(
    facts
      .map((fact, index) => ({ fact, index }))
      .sort((a, b) => rank(a.fact) - rank(b.fact) || a.index - b.index)
      .slice(0, limit)
      .map((entry) => entry.index),
  );
  return facts.filter((_, index) => kept.has(index));
}

// --- P2: COURSE-LEVEL MONEY ------------------------------------------------

/**
 * ONE money line per treatment course. Every figure is read STRAIGHT off the
 * course roll-up the wire already carries (deriveCourseLedger's output); this
 * builder adds NO formula and NO fallback -- it only chooses the honest WORDS
 * for the state the ledger already decided.
 *
 * CONTRACT 9a (COPY MAY NEVER OUT-CLAIM ITS TRUTH OWNER): a course reads
 * "Settled" only when the ledger itself says `settled`; a course whose
 * obligation size is unknown says so and never renders as zero.
 *
 * COURSE IDENTITY: this wire deliberately carries NO course or procedure
 * NAME (the clinical fence -- Patient 360 is the non-clinical hub), so a
 * line is identified by its course number. A name is never guessed.
 */
export interface PatientCourseMoneyLine {
  readonly courseId: number;
  /** "Course #96101" -- the course's lawful identity on THIS wire. */
  readonly courseLabel: string;
  /** The banked ledger-state label (JOURNEY_LEDGER_STATE_LABELS). */
  readonly ledgerLabel: string;
  /** "INR 8,000 due" / "Settled" / "Amount not configured" / ... */
  readonly dueLabel: string;
  readonly dueTone: AcBadgeTone;
  /** "INR 22,000 of INR 30,000 collected" / "INR 9,000 collected" / ... */
  readonly collectedLabel: string;
  readonly collectedMinorUnits: number;
  readonly outstandingMinorUnits: number | null;
  /**
   * FALSE when the obligation's SIZE is unknown to the ledger (an accepted
   * plan with no configured amount, or accepted items still awaiting their
   * visit close). An unknown obligation is NEVER counted as zero and never
   * lets a "settled" claim be made across courses.
   */
  readonly amountKnown: boolean;
}

/** The ruled per-state words + tone. AMBER (pending) = attention, the
 *  workflow continues; nothing here is RED, because an unpaid course does
 *  not block the desk (P5: red decoration without a blocker is forbidden). */
function courseDueView(
  ledgerState: Patient360TreatmentJourneyCourseWire['ledgerState'],
  outstandingMinorUnits: number | null,
): { label: string; tone: AcBadgeTone; amountKnown: boolean } {
  switch (ledgerState) {
    case 'outstanding':
      return {
        label:
          outstandingMinorUnits !== null
            ? `${formatMinorUnitsInr(outstandingMinorUnits)} due`
            : 'Plan remainder due',
        tone: 'pending',
        amountKnown: outstandingMinorUnits !== null,
      };
    case 'settled':
      return { label: 'Settled', tone: 'positive', amountKnown: true };
    case 'not_configured':
      return {
        label: 'Amount not configured',
        tone: 'pending',
        amountKnown: false,
      };
    case 'not_closed':
      return {
        label: 'Awaiting visit close',
        tone: 'neutral',
        amountKnown: false,
      };
    case 'no_accepted_plan':
      return {
        label: 'No accepted items yet',
        tone: 'neutral',
        amountKnown: true,
      };
    default:
      return { label: ledgerState, tone: 'neutral', amountKnown: false };
  }
}

/**
 * THE course-money lines, or NULL when this patient carries no readable
 * course lane at all (no journey on the wire / the lane is unavailable / no
 * courses). NULL is the caller's signal to keep the honest standing copy.
 *
 * A settled course is NOT a reason to return null: "settled" is money truth
 * the desk needs, and hiding it would be the page lying by omission.
 */
export function buildCourseMoneyLines(
  summary: Patient360SummaryResponse,
): readonly PatientCourseMoneyLine[] | null {
  const journey = summary.treatmentJourney;
  if (journey === undefined || journey.availability === 'unavailable') {
    return null;
  }
  if (journey.courses.length === 0) return null;
  return journey.courses.map((course) => {
    const due = courseDueView(course.ledgerState, course.outstandingMinorUnits);
    return {
      courseId: course.courseId,
      courseLabel: `Course #${course.courseId}`,
      ledgerLabel:
        JOURNEY_LEDGER_STATE_LABELS[course.ledgerState] ?? course.ledgerState,
      dueLabel: due.label,
      dueTone: due.tone,
      collectedLabel: courseCollectedLabel(
        course.collectedMinorUnits,
        course.outstandingMinorUnits,
      ),
      collectedMinorUnits: course.collectedMinorUnits,
      outstandingMinorUnits: course.outstandingMinorUnits,
      amountKnown: due.amountKnown,
    };
  });
}

/**
 * The collected line. The "of INR X" denominator is the LEDGER'S OWN
 * arithmetic identity and nothing else: in the `outstanding` state the owner
 * computed `outstanding = plan - collected` (lib/billing-queue.ts, the
 * course-grain netting), so `collected + outstanding` IS that plan, restated.
 * Every other state has no owner-proven plan total, so the weaker true claim
 * renders instead -- collected alone, never an inferred denominator.
 */
function courseCollectedLabel(
  collectedMinorUnits: number,
  outstandingMinorUnits: number | null,
): string {
  if (collectedMinorUnits <= 0) return 'Nothing collected yet';
  const collected = formatMinorUnitsInr(collectedMinorUnits);
  if (outstandingMinorUnits === null) return `${collected} collected`;
  const plan = formatMinorUnitsInr(collectedMinorUnits + outstandingMinorUnits);
  return `${collected} of ${plan} collected`;
}

/** The across-all-courses roll-up of the SAME lines -- a presentation total
 *  over the ONE owner's per-course figures, never a second derivation. */
export interface PatientCourseMoneyTotal {
  readonly courseCount: number;
  readonly dueMinorUnits: number;
  readonly collectedMinorUnits: number;
  /** Courses whose obligation SIZE the ledger does not know. */
  readonly unknownCourseCount: number;
  readonly line: string;
  readonly tone: AcBadgeTone;
}

/**
 * CONTRACT 9a, the binding case: this line NEVER says "fully settled".
 * It says "Nothing due" only when every course's obligation is both KNOWN
 * and met; with even one unknown-size obligation it states the weaker true
 * claim ("Nothing due where the amount is configured") and discloses the
 * count -- the NULL-AMOUNT OBLIGATION LAW carried into copy.
 */
export function buildCourseMoneyTotal(
  lines: readonly PatientCourseMoneyLine[],
): PatientCourseMoneyTotal {
  let dueMinorUnits = 0;
  let collectedMinorUnits = 0;
  let unknownCourseCount = 0;
  for (const line of lines) {
    collectedMinorUnits += line.collectedMinorUnits;
    if (line.outstandingMinorUnits !== null) {
      dueMinorUnits += line.outstandingMinorUnits;
    }
    if (!line.amountKnown) unknownCourseCount += 1;
  }
  const duePart =
    dueMinorUnits > 0
      ? `${formatMinorUnitsInr(dueMinorUnits)} due`
      : unknownCourseCount > 0
        ? 'Nothing due where the amount is configured'
        : 'Nothing due';
  const collectedPart =
    collectedMinorUnits > 0
      ? `${formatMinorUnitsInr(collectedMinorUnits)} collected`
      : 'nothing collected yet';
  const disclosure =
    unknownCourseCount > 0
      ? ` (${unknownCourseCount} course${
          unknownCourseCount === 1 ? '' : 's'
        } without a configured amount)`
      : '';
  return {
    courseCount: lines.length,
    dueMinorUnits,
    collectedMinorUnits,
    unknownCourseCount,
    line: `Across ${lines.length} course${
      lines.length === 1 ? '' : 's'
    }: ${duePart} - ${collectedPart}${disclosure}`,
    tone:
      dueMinorUnits > 0
        ? 'pending'
        : unknownCourseCount > 0
          ? 'neutral'
          : 'positive',
  };
}

// --- P1: JOURNEY LADDER MONOTONICITY --------------------------------------

/** The ladder's ruled ORDER (mirrors lib/domain/patient/patient-360.ts
 *  PATIENT_JOURNEY_SEGMENTS -- one order, mirrored, never re-invented). */
export const PATIENT_JOURNEY_SEGMENT_ORDER: readonly PatientJourneySegmentKey[] =
  [
    'booked',
    'arrived',
    'intake',
    'checked_in',
    'ready_for_doctor',
    'consultation',
    'checkout',
    'follow_up',
  ];

/** States that CLAIM the journey reached (or passed) this rung. */
const LADDER_CLAIMS_REACHED: ReadonlySet<PatientJourneySegmentState> = new Set([
  'complete',
  'current',
]);

/** The ONE state that asserts "the journey has NOT reached this rung yet".
 *  `not_evidenced` says the visit moved PAST a rung without evidence, and
 *  `unavailable` / `not_applicable` make no ordering claim at all -- so
 *  neither of those ever blocks a later rung. */
const LADDER_NOT_REACHED: ReadonlySet<PatientJourneySegmentState> = new Set([
  'pending',
]);

/**
 * The state relationships the BUSINESS MODEL explicitly allows (the founder's
 * P1 exemption). Key = the later rung; value = the earlier rungs it may
 * lawfully outrun.
 *
 *   intake before arrived -- PRE-CONSULT intake is a supported door: the
 *   patient fills the form before they walk in, so a completed intake beside
 *   an unreached arrival is TRUE, not a broken ladder.
 *
 * Growing this table is a founder act: it is the list of orderings the
 * product claims are real.
 */
export const JOURNEY_LADDER_ALLOWED_INVERSIONS: Readonly<
  Partial<Record<PatientJourneySegmentKey, readonly PatientJourneySegmentKey[]>>
> = {
  intake: ['arrived'],
};

/** Wire mirror of ONE journey-strip segment (the shape
 *  Patient360SummaryResponse.journey already carries, named so the ladder
 *  helpers below can speak it). */
export interface PatientJourneySegmentWire {
  readonly key: PatientJourneySegmentKey;
  readonly state: PatientJourneySegmentState;
}

export interface JourneyLadderBreak {
  /** The later rung making the unsupported claim. */
  readonly segment: PatientJourneySegmentKey;
  readonly claimed: PatientJourneySegmentState;
  /** The earlier rung the journey has not reached. */
  readonly blockedBy: PatientJourneySegmentKey;
}

/**
 * Every ordering claim the ladder makes that its own truth does not support:
 * a later rung claiming REACHED while an earlier rung still reads "not
 * reached", outside the allowed table. Pure -- the deterministic pin.
 */
export function journeyLadderOrderingBreaks(
  segments: readonly PatientJourneySegmentWire[],
): readonly JourneyLadderBreak[] {
  const rank = new Map<PatientJourneySegmentKey, number>(
    PATIENT_JOURNEY_SEGMENT_ORDER.map((key, index) => [key, index]),
  );
  const breaks: JourneyLadderBreak[] = [];
  for (const segment of segments) {
    if (!LADDER_CLAIMS_REACHED.has(segment.state)) continue;
    const here = rank.get(segment.key);
    if (here === undefined) continue;
    const allowed = JOURNEY_LADDER_ALLOWED_INVERSIONS[segment.key] ?? [];
    for (const earlier of segments) {
      const there = rank.get(earlier.key);
      if (there === undefined || there >= here) continue;
      if (!LADDER_NOT_REACHED.has(earlier.state)) continue;
      if (allowed.includes(earlier.key)) continue;
      breaks.push({
        segment: segment.key,
        claimed: segment.state,
        blockedBy: earlier.key,
      });
    }
  }
  return breaks;
}

/**
 * The ladder as it may lawfully RENDER: a rung whose ordering claim its own
 * truth does not support is demoted to `evidenced` -- the owner's OWN word
 * for "owned evidence WITHOUT a completion claim". No state is invented, no
 * evidence is dropped, and no truth owner moves: the strip simply stops
 * claiming an ordering the model never proved.
 */
export function clampJourneyLadderMonotonic(
  segments: readonly PatientJourneySegmentWire[],
): readonly PatientJourneySegmentWire[] {
  const broken = new Set(
    journeyLadderOrderingBreaks(segments).map((entry) => entry.segment),
  );
  if (broken.size === 0) return segments;
  return segments.map((segment) =>
    broken.has(segment.key)
      ? { key: segment.key, state: 'evidenced' as PatientJourneySegmentState }
      : segment,
  );
}

// --- P3: INLINE HISTORY CAPS ----------------------------------------------

/** The ruled inline caps (contract 8a: an inline history caps at 3-5 rows).
 *  The page's HEIGHT must not grow with how long the patient has been with
 *  the clinic, so every historical list on this carrier is capped and its
 *  full form opens in the shared DrawerShell. */
export const VISIT_HISTORY_INLINE_CAP = 3;
export const PAYMENT_HISTORY_INLINE_CAP = 5;

export interface CappedHistory<T> {
  readonly shown: readonly T[];
  readonly total: number;
  readonly capped: boolean;
  /** "Showing 3 of 7" -- null when nothing was withheld (never a lie by
   *  omission, never a cap line on an uncapped list). */
  readonly capLine: string | null;
}

export function capInlineHistory<T>(
  rows: readonly T[],
  cap: number,
): CappedHistory<T> {
  const total = rows.length;
  const limit = Math.max(0, cap);
  const capped = total > limit;
  const shown = capped ? rows.slice(0, limit) : rows;
  return {
    shown,
    total,
    capped,
    capLine: capped ? `Showing ${shown.length} of ${total}` : null,
  };
}

/** The full-history door label -- one grammar for every capped list here. */
export function fullHistoryDoorLabel(total: number, noun: string): string {
  return `View all ${total} ${noun}`;
}

/**
 * The HEADER line for a capped inline history (contract 8 + 8a: the cap is
 * stated in the header, so nobody opens or scrolls a list to learn how much
 * of it they are being shown).
 *
 * It returns null for an EMPTY list on purpose. A count line over nothing
 * ("0 of 0") is a fabricated claim: the list's own empty state already says
 * the truthful thing, and MULTI-VISIT-TREATMENT-JOURNEY-V1 / V6-P4 pins that
 * this carrier never renders a manufactured zero-count. Contract 9 -- copy
 * may never out-claim its truth owner -- makes the same demand.
 *
 * `windowed` is for a list the SERVER already truncated: the total in hand is
 * then not the patient's whole history, so the line says "most recent" and
 * never quotes a denominator the page cannot prove.
 */
export function inlineHistoryHeaderLine(
  capped: CappedHistory<unknown>,
  windowed = false,
): string | null {
  if (capped.capLine !== null) return capped.capLine;
  if (capped.total === 0) return null;
  if (windowed) return `Showing the ${capped.total} most recent`;
  return `Showing all ${capped.total}`;
}

// --- P5: CONSENT SEVERITY (founder ruling, unchanged policy) ---------------

export const CONSENT_NONE_STATE_LABEL = 'No consent recorded';

export interface PatientConsentStatusView {
  readonly label: string;
  readonly tone: AcBadgeTone;
}

/**
 * The consent STATUS chip. Missing consent is AMBER -- attention, the
 * workflow continues. It is NEVER red: red is reserved for a confirmed
 * blocking condition with a defined required action, and no ruling makes
 * recorded consent a precondition of treatment or payment today. Turning
 * this red is a POLICY decision (Deepak/Partha), never a visual one.
 */
export function consentStatusView(
  consentCount: number,
): PatientConsentStatusView {
  return consentCount > 0
    ? { label: `On file (${consentCount})`, tone: 'positive' }
    : { label: CONSENT_NONE_STATE_LABEL, tone: 'pending' };
}
